import {
  Ban,
  Check,
  Copy,
  ExternalLink,
  GitMerge,
  Loader,
  OctagonX,
  RefreshCw,
  Repeat,
  Sparkles,
  Undo2,
  Wrench,
} from 'lucide-react';
import { type ReactNode, useEffect, useState } from 'react';
import { prNumber } from '@/components/common/bits';
import { Button } from '@/components/ui/button';
import { AskBox, useAsk } from '@/features/actions/AskBox';
import { PLAN_QUEUED_NOTE, type Verb } from '@/features/actions/verbs';
import { useHubAction } from '@/hooks/use-hub-action';
import { api } from '@/lib/api';
import { ago, minutesSince } from '@/lib/format';
import { briefMovesTo, taskMovesTo } from '@/lib/optimistic';
import { ghPrCreateCommand, noPullRequestReason } from '@/lib/pr';
import {
  checksRunningLabel,
  confirmedSentence,
  describeFailingChecks,
  type ReviewGatePr,
  type ReviewGateState,
  type ReviewGateVerb,
  reviewGateState,
  reviewGateVerbs,
} from '@/lib/review-gate';
import { cn } from '@/lib/utils';

/**
 * The buttons at a gate.
 *
 * They are a quiet row: one filled accent button for the move that closes the
 * gate, hairline buttons for the alternatives, and a text-only destructive verb
 * that only picks up its tint on hover. A resting page therefore has exactly one
 * coloured control per gate, which is what lets a person scan four gates and see
 * four decisions rather than sixteen buttons.
 *
 * They grow to 36px below `sm`, because approving from a phone one-handed is a
 * real usage scene and a 28px target is not one.
 *
 * `data-gate-verb` names the two a keyboard can reach from the list: `e` presses
 * the primary, `x` the secondary. The key presses the button rather than calling
 * the mutation, so everything the button knows -- whether it is busy, whether it
 * opens a reason field first -- still holds for the keystroke.
 */
function Row({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn('flex flex-wrap items-center gap-1.5 px-gutter py-2', className)}>
      {children}
    </div>
  );
}

const TOUCH = 'max-sm:h-9 max-sm:px-3';

/** One unmerged dependency, named the way a person reads it: key, then PR if there is one. */
function depLabel(dep: { key: string; prUrl: string | null }): string {
  return `${dep.key}${prNumber(dep.prUrl).trim() ? ` (${prNumber(dep.prUrl).trim()})` : ''}`;
}

/** "T1 (#12)", or "T1 (#12) and T2" for more than one, in the order the graph gives them. */
function depList(waitsOn: readonly { key: string; prUrl: string | null }[]): string {
  const labels = waitsOn.map(depLabel);
  if (labels.length === 1) return labels[0]!;
  return `${labels.slice(0, -1).join(', ')} and ${labels.at(-1)}`;
}

/**
 * What a review gate says when there is no pull request to open (issue #120).
 *
 * The step is in `review` because its work is pushed and its gates passed; the
 * last step, opening the pull request, is the one that failed. The gate used to
 * draw an "Open PR" button that simply was not there, which told a person
 * nothing and left the branch to be found by hand. This says which branch,
 * repeats the worker's own reason, and hands over the one command that finishes
 * the job. Mark merged and the rest stay where they are: the decisions a person
 * has at this gate do not change because the link is missing.
 */
function NoPullRequest({
  branch,
  runSummary,
}: {
  branch?: string | null;
  runSummary?: string | null;
}) {
  const [copied, setCopied] = useState(false);
  const reason = noPullRequestReason(runSummary);
  const command = branch ? ghPrCreateCommand(branch) : null;

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 1600);
    return () => clearTimeout(timer);
  }, [copied]);

  return (
    <div className="bg-gate-bg/40 border-t px-gutter py-2">
      <p className="text-fg-2 max-w-prose text-xs">
        No pull request was opened
        {branch ? (
          <>
            {' for '}
            <span className="font-mono">{branch}</span>
          </>
        ) : null}
        . {reason ?? 'The run did not say why.'}
      </p>
      {command ? (
        <div className="mt-1.5 flex items-start gap-1.5">
          <pre className="bg-sunken dy-scroll min-w-0 flex-1 overflow-x-auto rounded-md border px-2.5 py-2 font-mono text-2xs select-all">
            {command}
          </pre>
          <Button
            size="icon-sm"
            variant="ghost"
            aria-label={copied ? 'Copied' : 'Copy this command'}
            onClick={() => {
              // A refused clipboard (no permission, an insecure origin) is not
              // an error to report: the command is on screen to select.
              void navigator.clipboard
                ?.writeText(command)
                .then(() => setCopied(true))
                .catch(() => {});
            }}
          >
            {copied ? <Check aria-hidden /> : <Copy aria-hidden />}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Open PR / Mark merged / Send back / Reassign / Cancel.
 *
 * `waitsOn` is this step's dependencies that have not merged yet (issue #24):
 * empty for the common case of a step with nothing stacked under it, or one
 * that depends only on work already landed. When it is not empty, marking
 * merged out of that order is exactly the mistake that breaks the base, so
 * the row says so and Mark merged asks a sharper question than usual rather
 * than the one every other PR gets.
 */
/**
 * What GitHub last said, drawn under the buttons in the same quiet register the
 * rest of the task page uses for timings (issue #125).
 *
 * Present in every state, including the ones nobody has confirmed. A verdict
 * with no timestamp beside it is exactly what a person could not tell apart
 * from a live one, and that is the whole reason this gate was offering verbs
 * that would fail if pressed.
 */
function PrStateLine({ pr, state }: { pr: ReviewGatePr; state: ReviewGateState }) {
  if (!pr.prUrl) return null;
  const confirmed = pr.prStateConfirmedAt
    ? confirmedSentence(pr, ago(minutesSince(pr.prStateConfirmedAt)))
    : confirmedSentence(pr, null);

  const what =
    state === 'red'
      ? `CI is red: ${describeFailingChecks(pr.prFailingChecks)}.`
      : state === 'conflict'
        ? 'The branch no longer merges into its base.'
        : state === 'running'
          ? `${checksRunningLabel(pr) ?? 'Checks are still running'}.`
          : state === 'green'
            ? 'Checks pass and GitHub says it merges cleanly.'
            : null;

  return (
    <p className="text-fg-3 px-gutter pb-2 text-xs">
      {what ? <span className={state === 'green' ? undefined : 'text-fg-2'}>{what} </span> : null}
      {confirmed}
    </p>
  );
}

/**
 * The verbs at a review gate, chosen by what GitHub says right now (issue #125).
 *
 * `lib/review-gate.ts` holds the table and the reasoning; this draws it. The
 * rule it exists to keep is one sentence long: the primary verb is never one
 * that would fail if clicked. So a red gate leads with Fix the PR, a conflicted
 * one with Rebase and fix, a gate whose checks are still running leads with a
 * disabled count, and only a gate GitHub says is green offers Mark merged at
 * all.
 *
 * Send back, Reassign and Cancel stay underneath in every state. They are
 * decisions about the work rather than about the pull request, and a person may
 * want any of them whatever GitHub thinks.
 *
 * `waitsOn` is this step's dependencies that have not merged yet (issue #24).
 * When it is not empty, marking merged out of that order is exactly the mistake
 * that breaks the base, so the row says so and Mark merged asks a sharper
 * question than usual.
 */
export function ReviewActions({
  taskId,
  prUrl,
  branch,
  runSummary,
  waitsOn,
  pr,
  className,
}: {
  taskId: string;
  prUrl?: string | null;
  /** The pushed branch, so a gate with no pull request can still name it (issue #120). */
  branch?: string | null;
  /** The run's own account, which is where the reason for a missing PR lives. */
  runSummary?: string | null;
  waitsOn?: readonly { key: string; prUrl: string | null }[];
  /**
   * What the hub last confirmed about this pull request. Absent on a caller
   * that does not carry it, which reads as `unknown` and draws the row exactly
   * as it looked before any of this existed.
   */
  pr?: ReviewGatePr | null;
  className?: string;
}) {
  const ask = useAsk();
  const blockers = waitsOn ?? [];
  const blocked = blockers.length > 0;
  const watched: ReviewGatePr = { ...(pr ?? {}), prUrl: pr?.prUrl ?? prUrl ?? null };
  const state = reviewGateState(watched);
  const verbs = reviewGateVerbs(state);
  const offers = (verb: ReviewGateVerb) => verbs.primary === verb || verbs.also.includes(verb);

  // One mutation for the whole row. Marking merged used to be a second hook that
  // wrote on the first press; it is a verb like the others now, and it asks.
  const action = useHubAction(
    async (verb: Verb, value: string) => {
      if (verb === 'markMerged') return api.markDone(taskId);
      // The hub re-reads GitHub before it records a merge, and answers 409 if
      // GitHub disagrees. This is the person overruling that, which they are
      // entitled to do: the hub has no merge permission, so the record has
      // always been a report of something a human did and knows about.
      if (verb === 'markMergedAnyway') return api.markDone(taskId, { force: true });
      if (verb === 'fixPr' || verb === 'rebaseAndFix') return api.fixPr(taskId);
      // `changes` cannot arrive empty: its field is required (see `verbs.ts`).
      if (verb === 'changes') return api.requeueTask(taskId, { feedback: value });
      if (verb === 'reassign')
        return api.requeueTask(taskId, { reassign: true, feedback: value || undefined });
      if (verb === 'cancel') return api.cancelTask(taskId, value || undefined);
      throw new Error(`no review action for ${verb}`);
    },
    {
      onDone: () => ask.close(),
      success: (verb) =>
        verb === 'markMerged' || verb === 'markMergedAnyway'
          ? 'Marked merged.'
          : verb === 'fixPr' || verb === 'rebaseAndFix'
            ? 'Filed an urgent fix task on the same branch.'
            : undefined,
      // Filing a fix leaves this task exactly where it is, on purpose: it is a
      // new task, and this one's record of having passed is not touched.
      optimistic: (verb) =>
        verb === 'fixPr' || verb === 'rebaseAndFix'
          ? // Nothing to move: the fix is a new task, and this one stays in
            // review with its record intact, which is the point.
            () => () => {}
          : taskMovesTo(
              taskId,
              verb === 'markMerged' || verb === 'markMergedAnyway'
                ? 'done'
                : verb === 'cancel'
                  ? 'cancelled'
                  : 'queued',
            ),
    },
  );

  const fixVerb = verbs.primary === 'rebaseAndFix' ? 'rebaseAndFix' : 'fixPr';
  const openPr = prUrl ? (
    <Button
      asChild
      size="sm"
      variant={verbs.primary === 'openPr' ? 'gate' : 'outline'}
      className={TOUCH}
    >
      <a href={prUrl} target="_blank" rel="noreferrer">
        <ExternalLink aria-hidden />
        {`Open PR${prNumber(prUrl)}`}
      </a>
    </Button>
  ) : null;

  return (
    <div className={className}>
      {blocked ? (
        <p className="text-gate bg-gate-bg/40 px-gutter py-1.5 text-xs">
          Not mergeable yet: waits on {depList(blockers)}.
        </p>
      ) : null}
      {prUrl ? null : <NoPullRequest branch={branch} runSummary={runSummary} />}
      <Row>
        {/* Fix the PR / Rebase and fix lead when GitHub says the branch cannot
            be merged. They file a NEW urgent task on the same branch rather
            than retrying this one: this task succeeded, and a retry would
            spend an attempt on a task that did nothing wrong. */}
        {verbs.primary === 'fixPr' || verbs.primary === 'rebaseAndFix' ? (
          <Button
            size="sm"
            variant="gate"
            data-gate-verb="primary"
            onClick={() => ask.open(fixVerb, taskId)}
            className={TOUCH}
          >
            <Wrench aria-hidden />
            {verbs.primary === 'rebaseAndFix' ? 'Rebase and fix' : 'Fix the PR'}
          </Button>
        ) : null}

        {/* Checks still running: a count, not a button. There is nothing safe
            to press yet, and an enabled verb here is the bug. */}
        {verbs.primary === 'checksRunning' ? (
          <Button size="sm" variant="gate" disabled className={TOUCH}>
            <Loader aria-hidden />
            {checksRunningLabel(watched) ?? 'Checks running'}
          </Button>
        ) : null}

        {offers('openPr') ? openPr : null}

        {offers('markMerged') ? (
          <Button
            size="sm"
            variant="outline"
            data-gate-verb={verbs.primary === 'openPr' ? 'primary' : undefined}
            onClick={() =>
              ask.open(
                'markMerged',
                taskId,
                undefined,
                blocked
                  ? `Mark this PR merged? It waits on ${depList(blockers)}, which ${blockers.length > 1 ? 'have' : 'has'} not merged yet. Merging out of order is on you.`
                  : undefined,
              )
            }
            className={TOUCH}
          >
            <GitMerge aria-hidden />
            Mark merged
          </Button>
        ) : null}

        <Button
          size="sm"
          variant="outline"
          data-gate-verb="secondary"
          onClick={() => ask.open('changes', taskId)}
          className={TOUCH}
        >
          <Undo2 aria-hidden />
          Send back
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => ask.open('reassign', taskId)}
          className={TOUCH}
        >
          <Repeat aria-hidden />
          Reassign
        </Button>
        <Button
          size="sm"
          variant="danger"
          onClick={() => ask.open('cancel', taskId)}
          className={TOUCH}
        >
          <Ban aria-hidden />
          Cancel
        </Button>
      </Row>
      <PrStateLine pr={watched} state={state} />
      {/* The hub refused to record a merge because GitHub disagreed at the
          moment of the click. Said before anything was written, which is the
          whole point: unwinding a recorded merge is a conversation. */}
      {action.error && /GitHub says/.test(action.error) ? (
        <div className="px-gutter pb-2">
          <p className="text-bad max-w-prose text-xs">{action.error}</p>
          <Button
            size="xs"
            variant="outline"
            className="mt-1.5"
            onClick={() => action.run('markMergedAnyway', '')}
            disabled={action.busy}
          >
            <GitMerge aria-hidden />
            Record it anyway
          </Button>
        </div>
      ) : null}
      {ask.ask ? (
        <AskBox
          ask={ask.ask}
          busy={action.busy}
          error={action.error}
          onCancel={ask.close}
          onConfirm={(value) => {
            if (ask.ask) action.run(ask.ask.verb, value);
          }}
        />
      ) : null}
    </div>
  );
}

/**
 * Request plan, on a draft nobody is planning yet.
 *
 * No reason field and no confirm step: this asks a machine to draft something a
 * person then approves, so it is the constructive move at this gate rather than a
 * consequential one, and the plan itself can still be rejected. It wears the
 * accent for the same reason Go does - it is the one thing this brief is waiting
 * for.
 */
export function RequestPlanButton({ briefId, className }: { briefId: string; className?: string }) {
  const plan = useHubAction(() => api.requestPlan(briefId), {
    success: `Plan requested. ${PLAN_QUEUED_NOTE}`,
  });
  return (
    <Button
      size="sm"
      variant="gate"
      onClick={() => plan.run()}
      disabled={plan.busy}
      className={cn(TOUCH, className)}
    >
      <Sparkles aria-hidden />
      {plan.busy ? 'Asking…' : 'Request plan'}
    </Button>
  );
}

/** The same verb as the row of a gate, for the brief's own pane. */
export function RequestPlanActions({
  briefId,
  className,
}: {
  briefId: string;
  className?: string;
}) {
  return (
    <div className={className}>
      <Row>
        <RequestPlanButton briefId={briefId} />
      </Row>
    </div>
  );
}

/** Go / Reject, at the plan gate. */
export function PlanActions({ briefId, className }: { briefId: string; className?: string }) {
  const ask = useAsk();
  const action = useHubAction(
    async (verb: Verb, value: string) => {
      if (verb === 'approve') return api.approveBrief(briefId);
      if (verb === 'reject') return api.rejectBrief(briefId, value || undefined);
      throw new Error(`no plan action for ${verb}`);
    },
    {
      onDone: () => ask.close(),
      // The tasks an approval queues are the server's to report; all this claims
      // is that the brief has left the gate, which is the row the person is
      // looking at.
      optimistic: (verb) => briefMovesTo(briefId, verb === 'approve' ? 'approved' : 'rejected'),
    },
  );

  return (
    <div className={className}>
      <Row>
        <Button
          size="sm"
          variant="gate"
          data-gate-verb="primary"
          onClick={() => ask.open('approve', briefId)}
          className={TOUCH}
        >
          <Check aria-hidden />
          Go - approve plan
        </Button>
        <Button
          size="sm"
          variant="danger"
          data-gate-verb="secondary"
          onClick={() => ask.open('reject', briefId)}
          className={TOUCH}
        >
          <Ban aria-hidden />
          Reject
        </Button>
      </Row>
      {ask.ask ? (
        <AskBox
          ask={ask.ask}
          busy={action.busy}
          error={action.error}
          onCancel={ask.close}
          onConfirm={(value) => {
            if (ask.ask) action.run(ask.ask.verb, value);
          }}
        />
      ) : null}
    </div>
  );
}

/**
 * Cancel brief, on a brief that is past Go.
 *
 * The only verb an approved brief has: rejection is the pre-Go refusal and the
 * hub now says so, because flipping an approved brief to `rejected` left its
 * tasks running and their PRs landing on a brief the record said nobody wanted.
 * This one stops the work. It asks for a reason and will not confirm without
 * one, since that note is the only account of why a running job was killed.
 */
export function CancelBriefActions({
  briefId,
  className,
}: {
  briefId: string;
  className?: string;
}) {
  const ask = useAsk();
  const cancel = useHubAction((reason: string) => api.cancelBrief(briefId, reason), {
    onDone: () => ask.close(),
    // `rejected` is what the hub sets a cancelled brief to: the record has one
    // closed-without-finishing state, and the journal line is what says which of
    // the two verbs closed it.
    optimistic: () => briefMovesTo(briefId, 'rejected'),
  });

  return (
    <div className={className}>
      <Row>
        <Button
          size="sm"
          variant="danger"
          onClick={() => ask.open('cancelBrief', briefId)}
          className={TOUCH}
        >
          <OctagonX aria-hidden />
          Cancel job
        </Button>
      </Row>
      {ask.ask ? (
        <AskBox
          ask={ask.ask}
          busy={cancel.busy}
          error={cancel.error}
          onCancel={ask.close}
          onConfirm={(value) => cancel.run(value)}
        />
      ) : null}
    </div>
  );
}

/**
 * Requeue / Cancel, on a failure.
 *
 * A failure a person is looking at has usually spent its attempts - that is why
 * it is failed rather than queued - and the button said only "Requeue", which
 * reads as "try that again" and hides what the hub actually does about the
 * ceiling. It raises it by one (see `requeueTask`), so the last attempt is
 * granted rather than refused, and the label now says so. It stays enabled,
 * because the write succeeds: disabling it would take away the one verb this
 * task has.
 */
export function FailureActions({
  taskId,
  attempts,
  maxAttempts,
  className,
}: {
  taskId: string;
  /** From the task, so the label can say whether this is the extra attempt. */
  attempts: number;
  maxAttempts: number;
  className?: string;
}) {
  const ask = useAsk();
  const requeue = useHubAction(() => api.requeueTask(taskId), {
    success: 'Back in the queue.',
    optimistic: () => taskMovesTo(taskId, 'queued'),
  });
  const cancel = useHubAction((reason: string) => api.cancelTask(taskId, reason || undefined), {
    onDone: () => ask.close(),
    optimistic: () => taskMovesTo(taskId, 'cancelled'),
  });
  const spent = attempts >= maxAttempts;

  return (
    <div className={className}>
      <Row>
        <Button
          size="sm"
          variant="outline"
          onClick={() => requeue.run()}
          disabled={requeue.busy}
          className={TOUCH}
          title={
            spent
              ? `Retries are exhausted (${attempts} of ${maxAttempts}). Requeueing grants attempt ${attempts + 1}.`
              : undefined
          }
        >
          <RefreshCw aria-hidden />
          {spent ? 'Requeue with one more attempt' : 'Requeue'}
        </Button>
        <Button
          size="sm"
          variant="danger"
          onClick={() => ask.open('cancel', taskId)}
          className={TOUCH}
        >
          <Ban aria-hidden />
          Cancel
        </Button>
      </Row>
      {ask.ask ? (
        <AskBox
          ask={ask.ask}
          busy={cancel.busy}
          error={cancel.error}
          onCancel={ask.close}
          onConfirm={(value) => cancel.run(value)}
        />
      ) : null}
    </div>
  );
}

/**
 * Try it again / Cancel, on a task the scheduler is holding.
 *
 * A held task is queued, not failed, so the failure card never drew and the one
 * verb that unsticks it had no button anywhere in the console: a person looking
 * at their own job could read "held: it already failed on all 1 eligible
 * worker(s); a human should look at it" and had nothing to press. Requeueing a
 * queued task is exactly the override the hub means by it - `requeueTask` clears
 * the hold and the tried-worker list, which on a one-worker repo is the only way
 * out - so this is that verb, where the hold is read.
 */
export function HeldActions({ taskId, className }: { taskId: string; className?: string }) {
  const ask = useAsk();
  const requeue = useHubAction(() => api.requeueTask(taskId), {
    success: 'Back in the queue.',
    optimistic: () => taskMovesTo(taskId, 'queued'),
  });
  const cancel = useHubAction((reason: string) => api.cancelTask(taskId, reason || undefined), {
    onDone: () => ask.close(),
    optimistic: () => taskMovesTo(taskId, 'cancelled'),
  });

  return (
    <div className={className}>
      <Row>
        <Button
          size="sm"
          onClick={() => requeue.run()}
          disabled={requeue.busy}
          className={TOUCH}
          title="Clears the hold and the list of workers that already tried, so this task is offered again."
        >
          <RefreshCw aria-hidden />
          Try it again
        </Button>
        <Button
          size="sm"
          variant="danger"
          onClick={() => ask.open('cancel', taskId)}
          className={TOUCH}
        >
          <Ban aria-hidden />
          Cancel
        </Button>
      </Row>
      {requeue.error ? <p className="text-bad px-gutter pb-2 text-xs">{requeue.error}</p> : null}
      {ask.ask ? (
        <AskBox
          ask={ask.ask}
          busy={cancel.busy}
          error={cancel.error}
          onCancel={ask.close}
          onConfirm={(value) => cancel.run(value)}
        />
      ) : null}
    </div>
  );
}

/**
 * The same verb as one control, for a list row rather than a card: the plan in
 * the job pane, where a held step is read before anybody thinks to open it.
 */
export function RetryHeldButton({ taskId }: { taskId: string }) {
  const requeue = useHubAction(() => api.requeueTask(taskId), {
    success: 'Back in the queue.',
    optimistic: () => taskMovesTo(taskId, 'queued'),
  });

  return (
    <Button
      variant="outline"
      size="xs"
      className="shrink-0"
      onClick={() => requeue.run()}
      disabled={requeue.busy}
      title="Clears the hold and the list of workers that already tried, so this task is offered again."
    >
      <RefreshCw aria-hidden />
      Try again
    </Button>
  );
}

/** The only verb an admin has on something still in flight. */
export function CancelOnly({ taskId, className }: { taskId: string; className?: string }) {
  const ask = useAsk();
  const cancel = useHubAction((reason: string) => api.cancelTask(taskId, reason || undefined), {
    onDone: () => ask.close(),
    optimistic: () => taskMovesTo(taskId, 'cancelled'),
  });

  return (
    <div className={className}>
      <Row>
        <Button
          size="sm"
          variant="danger"
          onClick={() => ask.open('cancel', taskId)}
          className={TOUCH}
        >
          <Ban aria-hidden />
          Cancel task
        </Button>
      </Row>
      {ask.ask ? (
        <AskBox
          ask={ask.ask}
          busy={cancel.busy}
          error={cancel.error}
          onCancel={ask.close}
          onConfirm={(value) => cancel.run(value)}
        />
      ) : null}
    </div>
  );
}
